import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { notificationManager } from '@2cmstudio/notifications'
import { Button } from './Button'
import { useUserChallenges } from '@/features/challenges/UserChallengeProvider'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { rescheduleAllNotifications } from '@/utils/simpleChallengeUtils'

interface PostChallengeNotificationPromptProps {
  challengeTitle: string
  onDismiss: () => void
}

export function PostChallengeNotificationPrompt({ 
  challengeTitle, 
  onDismiss 
}: PostChallengeNotificationPromptProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { challengeRepository } = useUserChallenges()
  const [showPrompt, setShowPrompt] = useState(false)
  const [isRequesting, setIsRequesting] = useState(false)

  const { data: challenges = [] } = useQuery({
    queryKey: ['challenges'],
    queryFn: () => challengeRepository.listByOwner(user?.uid),
  })

  useEffect(() => {
    // Check if we should show the notification prompt
    const checkNotificationStatus = async () => {
      try {
        if (!notificationManager.isNotificationSupported()) {
          return
        }

        // Get current permission status (fresh from browser)
        const currentPermission = 'Notification' in window ? Notification.permission : 'denied'
        const notificationsEnabled = localStorage.getItem('notifications-enabled') !== 'false'
        const hasAskedAfterCreation = localStorage.getItem('notification-asked-after-creation') === 'true'
        
        if (import.meta.env.DEV) {
          console.log('📱 Post-Challenge Notification Check:', {
            currentPermission,
            notificationsEnabled,
            hasAskedAfterCreation
          })
        }
        
        // Show prompt if:
        // 1. Permissions are not granted (default or denied)
        // 2. AND we haven't asked after creation before
        const needsPermission = currentPermission !== 'granted'
        const shouldShow = needsPermission && !hasAskedAfterCreation
        
        if (import.meta.env.DEV) {
          console.log('📱 Post-Challenge Decision:', {
            needsPermission,
            shouldShow
          })
        }
        
        setShowPrompt(shouldShow)
      } catch (error) {
        console.error('Failed to check notification status:', error)
      }
    }

    checkNotificationStatus()
  }, [])

  const handleEnableNotifications = async () => {
    setIsRequesting(true)
    
    try {
      const currentPermission = 'Notification' in window ? Notification.permission : 'denied'
      
      let finalPermission = currentPermission
      
      // Only request permission if not already granted
      if (currentPermission !== 'granted') {
        finalPermission = await notificationManager.requestPermission()
      }
      
      if (finalPermission === 'granted') {
        // Enable notifications in localStorage
        localStorage.setItem('notifications-enabled', 'true')
        localStorage.setItem('notification-asked-after-creation', 'true')
        
        // Reschedule notifications for all active challenges
        try {
          await rescheduleAllNotifications(challenges)
          if (import.meta.env.DEV) {
            console.log('✅ Notifications enabled and scheduled after challenge creation')
          }
        } catch (error) {
          console.error('Failed to reschedule notifications:', error)
        }
        
        onDismiss()
      } else {
        if (import.meta.env.DEV) {
          console.log('❌ Notifications denied after challenge creation')
        }
        localStorage.setItem('notification-asked-after-creation', 'true')
        onDismiss()
      }
    } catch (error) {
      console.error('Failed to request notification permission:', error)
    } finally {
      setIsRequesting(false)
    }
  }

  const handleMaybeLater = () => {
    localStorage.setItem('notification-asked-after-creation', 'true')
    onDismiss()
  }

  if (!showPrompt) {
    return null
  }

  return (
    <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-xl p-lg mb-xl">
      <div className="text-center space-y-md">
        <div className="text-4xl mb-md">📱</div>
        <h3 className="text-lg font-semibold text-text">
          {t('notifications.stayOnTrackWith', { name: challengeTitle })}
        </h3>
        <p className="text-sm text-text-muted leading-relaxed max-w-md mx-auto">
          {t('notifications.getTimelyReminders')} 
          {t('notifications.notificationsWillHelp')}
        </p>
        
        <div className="flex flex-col sm:flex-row gap-sm justify-center pt-md">
          <Button
            variant="primary"
            size="sm"
            onClick={handleEnableNotifications}
            disabled={isRequesting}
            className="min-w-[140px]"
          >
            {isRequesting ? t('common.enabling') : t('notifications.enableReminders')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleMaybeLater}
            className="min-w-[140px]"
          >
            {t('common.mayBeLater')}
          </Button>
        </div>
        
        <p className="text-xs text-text-faint">
          {t('notifications.changeAnytime')}
        </p>
      </div>
    </div>
  )
} 